import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  MapPin, ShieldCheck, Landmark, BellRing, Wallet, ReceiptText, Gift, LayoutDashboard, Search, ArrowRight, Crosshair, Loader2,
  Fingerprint, Link2, Stamp, Radar, Zap, Server, CheckCircle2, Activity, ChevronDown, Terminal, Satellite, Cpu, Globe2,
  SignalHigh, Lock, Boxes,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/hooks";
import { useUserLocation, placeLabel } from "@/lib/location";
import { money } from "@/lib/api";
import { GeoMap } from "@/components/GeoMap";

const MODULES = [
  { icon: MapPin, t: "Comunidad", d: "Reportes con GPS de alta precisión, folio único y seguimiento en vivo.", tag: "01" },
  { icon: BellRing, t: "Omnicanalidad", d: "Confirmaciones y cambios de estatus por WhatsApp Business con acuse de lectura.", tag: "02", sim: true },
  { icon: LayoutDashboard, t: "Intranet", d: "Tareas interdependencias, incidencias y tableros ejecutivos en tiempo real.", tag: "03" },
  { icon: Wallet, t: "Ventanilla y pagos", d: "Expediente digital y liquidación instantánea o domiciliada con Cuenta Nu.", tag: "04", sim: true },
  { icon: ShieldCheck, t: "Validez legal", d: "Firma digital avanzada, sello NOM-151 y anclaje inmutable en Polygon.", tag: "05", sim: true },
  { icon: ReceiptText, t: "Multas y cobranza", d: "Boletas QR con evidencia geolocalizada, cuadrillas en vivo y mapa de calor.", tag: "06" },
  { icon: Gift, t: "Lealtad HGT", d: "Tokens por cumplimiento canjeables en comercios y servicios municipales.", tag: "07", sim: true },
];
const PIPE = [[MapPin, "Reporte GPS"], [Fingerprint, "Folio + SHA-256"], [BellRing, "WhatsApp"], [Radar, "Intranet"], [Stamp, "Firma + NOM-151"], [Link2, "Anclaje Polygon"]];
const STACK = ["React", "FastAPI serverless", "MongoDB", "MapLibre globe · Carto", "deck.gl", "Chart.js", "ECDSA P-256", "NOM-151", "Polygon", "WhatsApp API", "Cuenta Nu", "Webhooks HMAC"];
const EVENT_LABEL = { "reporte.creado": "Reporte creado", "pago.liquidado": "Pago liquidado", "multa.emitida": "Multa emitida", "tramite.iniciado": "Trámite iniciado", "tramite.emitido": "Documento emitido" };
const evLabel = (e) => EVENT_LABEL[e] || e.replace(".", " · ").replace("_", " ");
const ago = (s) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 60000));
  if (m < 1) return "ahora"; if (m < 60) return `hace ${m} min`; const h = Math.round(m / 60); if (h < 24) return `hace ${h} h`; return `hace ${Math.round(h / 24)} d`;
};

function Counter({ value, format = (v) => Math.round(v).toLocaleString("es-MX"), testid, duration = 1600 }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (value == null) return undefined;
    let raf, start;
    const el = ref.current;
    const run = () => {
      const tick = (t) => { if (!start) start = t; const k = Math.min(1, (t - start) / duration); setV(value * (1 - Math.pow(1 - k, 3))); if (k < 1) raf = requestAnimationFrame(tick); };
      raf = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { run(); io.disconnect(); } });
    if (el) io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value, duration]);
  return <span ref={ref} data-testid={testid}>{format(v)}</span>;
}

/** Reloj UTC-5 con parpadeo tipo centro de mando. */
function LiveClock() {
  const [n, setN] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setN(new Date()), 1000); return () => clearInterval(t); }, []);
  const hh = String(n.getHours()).padStart(2, "0");
  const mm = String(n.getMinutes()).padStart(2, "0");
  const ss = String(n.getSeconds()).padStart(2, "0");
  return <span className="font-mono tabular-nums" data-testid="hero-clock">{hh}<span className="flicker">:</span>{mm}<span className="flicker">:</span>{ss} UTC-5</span>;
}

export default function Landing() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const pulse = useFetch("/public/pulse", { poll: 15000 });
  const loc = useUserLocation();
  const [evIdx, setEvIdx] = useState(0);
  useEffect(() => { if (!loading && user) navigate("/app", { replace: true }); }, [user, loading, navigate]);
  // El landing tiene su propia paleta (siempre command-center)
  useEffect(() => { document.body.classList.add("landing"); return () => document.body.classList.remove("landing"); }, []);
  const events = useMemo(() => pulse.data?.events || [], [pulse.data]);
  const s = pulse.data?.stats;
  useEffect(() => { const t = setInterval(() => setEvIdx((i) => i + 1), 3200); return () => clearInterval(t); }, []);
  const ev = events.length ? events[evIdx % events.length] : null;
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const sampleFolio = events.find((e) => e.folio?.startsWith("QR-RPT"))?.folio || "QR-RPT-2026-000001";
  const locActive = loc.status === "granted" && !!loc.coords;

  return (
    <div className="min-h-screen bg-[#F7FBFC] text-foreground">
      {/* ─────────── Header flotante ─────────── */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3">
          <div className="glass-dark rounded-full h-14 flex items-center gap-6 px-3 pr-2">
            <div className="flex items-center gap-2.5 pl-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0F4C5C] text-white glow-border"><Landmark className="h-5 w-5" /></span>
              <div className="leading-tight text-white">
                <div className="font-display font-semibold text-base tracking-tight">HitchGob</div>
                <div className="text-[9px] tracking-[0.22em] uppercase text-white/50">Quintana Roo · OS</div>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6 mx-auto text-sm text-white/80">
              {[["modulos", "Módulos"], ["beneficios", "Beneficios"], ["plataforma", "Plataforma"]].map(([id, l]) => (
                <button key={id} onClick={() => go(id)} className="hover:text-white transition-colors" data-testid={`landing-nav-${id}`}>{l}</button>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-2 text-[10px] text-white/60 font-mono mr-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
              <LiveClock />
            </div>
            <div className="flex gap-2 ml-auto md:ml-0">
              <Button variant="outline" className="rounded-full hidden sm:inline-flex border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white" onClick={() => navigate("/verificar")} data-testid="header-verify-link">Verificar folio</Button>
              <Button className="rounded-full bg-white text-[#06151D] hover:bg-[#DFF3F6] btn-press" onClick={login} data-testid="header-login-button">Iniciar sesión</Button>
            </div>
          </div>
        </div>
      </header>

      {/* ─────────── HERO command-center ─────────── */}
      <section className="tech-hero relative overflow-hidden diagonal-split pt-24">
        <div className="absolute inset-0 tech-grid pointer-events-none" />
        <div className="absolute inset-0 tech-scan pointer-events-none" />
        {/* mapa de fondo gigante */}
        <div className="absolute inset-0 opacity-[0.7]" data-testid="hero-globe">
          <GeoMap theme="dark" className="h-full rounded-none border-0" testid="hero-globe-map" hud={false} interactive={false} autoRotate
            center={[19.6, -97.5]} zoom={2.6} pitch={0} bearing={0} reports={pulse.data?.points || []} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#061820] via-[#06182099] to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 lg:py-24">
          {/* chips de telemetría arriba */}
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono text-[#B9E7EE] tracking-[0.18em] uppercase">
            <span className="chip-glow rounded-full px-3 py-1 inline-flex items-center gap-2"><span className="live-dot h-2 w-2 rounded-full bg-emerald-400" />Sistema en línea</span>
            <span className="chip-glow rounded-full px-3 py-1 inline-flex items-center gap-2"><Satellite className="h-3 w-3" />GPS activo</span>
            <span className="chip-glow rounded-full px-3 py-1 inline-flex items-center gap-2"><Lock className="h-3 w-3" />SHA-256 · NOM-151</span>
            <span className="chip-glow rounded-full px-3 py-1 inline-flex items-center gap-2"><Globe2 className="h-3 w-3" />Polygon · verificable</span>
            {s?.chain_valid && <span className="chip-glow rounded-full px-3 py-1 inline-flex items-center gap-2 text-emerald-300"><CheckCircle2 className="h-3 w-3" />Cadena íntegra</span>}
          </div>

          <div className="mt-8 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
            <div className="fade-up">
              <div className="inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.28em] uppercase text-[#7fd6e2]">
                <span className="h-px w-10 bg-[#7fd6e2]/60" />Centro de mando ciudadano
              </div>
              <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold tracking-[-0.04em] leading-[0.9] text-white">
                El <span className="relative inline-block">
                  <span className="glow-cyan text-[#7fd6e2]">sistema operativo</span>
                </span><br />
                <span className="text-white/95">digital</span> de <span className="text-[#7fd6e2]">Quintana Roo</span>
              </h1>
              <p className="mt-7 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
                Reportes ciudadanos con <strong className="text-white">GPS exacto</strong>, dependencias conectadas, trámites, pagos, multas y cobranza en campo — cada evento <strong className="text-white">firmado, sellado y anclado</strong> en una sola plataforma.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Button size="lg" className="rounded-full h-13 px-7 bg-white text-[#06151D] hover:bg-[#DFF3F6] btn-press font-semibold shadow-[0_14px_40px_-10px_rgba(127,214,226,0.6)]" onClick={login} data-testid="login-google-button">
                  Entrar con Google <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="rounded-full h-13 px-7 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white btn-press" onClick={() => go("modulos")} data-testid="hero-explore-button">
                  <Boxes className="h-4 w-4 mr-2" />Explorar módulos
                </Button>
              </div>

              {/* Tarjeta de ubicación integrada */}
              <div className="mt-9 glass-dark rounded-2xl p-4 flex items-center gap-3 max-w-md glow-border" data-testid="landing-location-card">
                <span className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${locActive ? "bg-emerald-500/15 text-emerald-300" : "bg-[#7fd6e2]/15 text-[#7fd6e2]"} radar`}>
                  <Crosshair className="h-5 w-5 relative z-10" />
                </span>
                {locActive ? (
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate" data-testid="landing-location-label">{placeLabel(loc.place) || "Ubicación detectada"}</div>
                    <div className="text-[11px] text-white/50 font-mono tabular-nums">
                      {loc.coords.lat.toFixed(5)}, {loc.coords.lng.toFixed(5)} · ±{loc.coords.accuracy} m{loc.place?.municipio ? ` · ${loc.place.municipio}` : ""}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white">{loc.status === "denied" ? "Ubicación bloqueada en el navegador" : "Activa ubicación exacta"}</div>
                      <div className="text-[11px] text-white/55">{loc.status === "denied" ? "Habilítala desde el candado de la barra de direcciones." : "Permítelo para ver tu posición en el mapa en vivo."}</div>
                    </div>
                    <Button size="sm" variant="secondary" className="rounded-full bg-[#7fd6e2] text-[#06151D] hover:bg-white" onClick={loc.request} disabled={loc.status === "locating"} data-testid="landing-location-button">
                      {loc.status === "locating" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activar"}
                    </Button>
                  </>
                )}
              </div>

              {/* HUD inferior con stats numéricos */}
              <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl" data-testid="hero-hud">
                {[
                  { l: "Eventos anclados", v: s?.ledger, hint: "bloques verificables" },
                  { l: "Reportes", v: s?.reports, hint: "con GPS exacto" },
                  { l: "Recaudado", v: s?.revenue, f: (v) => money(v).replace(".00", ""), hint: "liquidado digital" },
                ].map((k) => (
                  <div key={k.l} className="relative glass-dark rounded-xl p-3">
                    <span className="corner-mark tl" /><span className="corner-mark tr" /><span className="corner-mark bl" /><span className="corner-mark br" />
                    <div className="text-[9px] uppercase tracking-[0.22em] text-white/50">{k.l}</div>
                    <div className="font-display text-2xl md:text-3xl font-bold text-white tabular-nums mt-1">
                      {k.v == null ? "—" : <Counter value={k.v} format={k.f} />}
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">{k.hint}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Panel derecho tipo centro de mando */}
            <div className="relative fade-up lg:mt-6">
              <div className="relative glass-dark rounded-2xl overflow-hidden glow-border">
                <span className="corner-mark tl" /><span className="corner-mark tr" /><span className="corner-mark bl" /><span className="corner-mark br" />
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-black/20">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-white/70">
                    <span className="flex gap-1"><span className="h-2 w-2 rounded-full bg-[#ff5f57]" /><span className="h-2 w-2 rounded-full bg-[#febc2e]" /><span className="h-2 w-2 rounded-full bg-[#28c840]" /></span>
                    <Terminal className="h-3 w-3 ml-2" />ops.hitchgob.qroo
                  </div>
                  <div className="text-[10px] font-mono text-emerald-400 flex items-center gap-1"><span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />LIVE</div>
                </div>

                {/* Mini mapa en vivo */}
                <div className="relative h-[260px] bg-[#061820]">
                  <GeoMap theme="dark" className="h-full rounded-none border-0" testid="landing-live-map" hud={false} scrollZoom={false}
                    center={[19.9, -87.7]} zoom={5.7} pitch={48} bearing={-18} reports={pulse.data?.points || []} />
                  <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white/60 bg-black/40 backdrop-blur px-2 py-1.5 rounded">
                    {[["#5FD3E0", "Recibido"], ["#2EE6C8", "Validado"], ["#409CFF", "Proceso"], ["#78E88C", "Resuelto"], ["#7fd6e2", "Sede"]].map(([c, l]) => (
                      <span key={l} className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: c, boxShadow: `0 0 6px ${c}` }} />{l}</span>
                    ))}
                  </div>
                  <div className="absolute top-0 left-0 right-0 h-0.5 overflow-hidden"><div className="h-full w-1/3 data-bar" /></div>
                </div>

                {/* Evento en vivo */}
                <div className="px-4 py-3 border-t border-white/10 bg-black/20" data-testid="landing-live-event">
                  <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-white/50">
                    <span className="flex items-center gap-1.5"><Activity className="h-3 w-3 text-emerald-400" />Evento más reciente</span>
                    <span className="live-dot h-2 w-2 rounded-full bg-emerald-400" />
                  </div>
                  {ev ? (
                    <div key={evIdx} className="fade-up mt-2 grid grid-cols-[1fr_auto] gap-3 items-center">
                      <div className="min-w-0">
                        <div className="font-medium text-white text-sm">{evLabel(ev.event)}</div>
                        <div className="font-mono text-xs text-[#7fd6e2]">{ev.folio}</div>
                        <div className="font-mono text-[10px] text-white/40 truncate">tx {ev.anchor?.tx_hash?.slice(0, 14)}…{ev.anchor?.tx_hash?.slice(-6)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-white/50">bloque</div>
                        <div className="font-mono text-lg text-white tabular-nums">#{ev.index}</div>
                      </div>
                    </div>
                  ) : <div className="mt-2 h-14 rounded bg-white/5 animate-pulse" />}
                </div>

                {/* Mini terminal con request/response */}
                <div className="px-4 py-3 border-t border-white/10 font-mono text-[11px] leading-relaxed text-[#c9eef3]/90">
                  <div className="text-white/40">$ POST /api/reports → <span className="text-emerald-400">201 Created</span></div>
                  <div className="mt-1 text-white/85">{"{"} folio: <span className="text-[#fce7c3]">"{sampleFolio}"</span>, hash: <span className="text-[#fce7c3]">"sha256:…"</span>, anchor: <span className="text-[#fce7c3]">"polygon-amoy"</span> {"}"}</div>
                </div>
              </div>

              {/* chips flotantes */}
              <div className="hidden md:flex items-center gap-2 absolute -top-4 -left-4 chip-glow rounded-full px-3 py-1.5 text-[11px] text-white float-slow">
                <Cpu className="h-3 w-3 text-[#7fd6e2]" /><span className="font-mono">FastAPI · serverless</span>
              </div>
              <div className="hidden md:flex items-center gap-2 absolute -bottom-4 -right-4 chip-glow rounded-full px-3 py-1.5 text-[11px] text-white float-slower">
                <SignalHigh className="h-3 w-3 text-emerald-400" /><span className="font-mono">99.9% uptime</span>
              </div>
            </div>
          </div>

          <button onClick={() => go("beneficios")} className="hidden lg:flex mx-auto mt-14 flex-col items-center text-[10px] tracking-[0.4em] text-white/40 hover:text-white/70 transition-colors" aria-label="Desliza">
            DESLIZA PARA EXPLORAR <ChevronDown className="h-4 w-4 mt-1.5 animate-bounce" />
          </button>
        </div>
      </section>

      {/* ─────────── TICKER neón ─────────── */}
      <div className="ticker-neon text-white overflow-hidden" data-testid="landing-ticker">
        <div className="marquee flex gap-10 py-3 whitespace-nowrap text-xs font-mono">
          {[...events, ...events].map((e, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
              <span className="text-white">{evLabel(e.event)}</span>
              <span className="text-[#7fd6e2]">{e.folio}</span>
              · tx {e.anchor?.tx_hash?.slice(0, 10)}…
              · <span className="text-white/50">{ago(e.timestamp)}</span>
            </span>
          ))}
        </div>
      </div>

      {/* ─────────── BENEFICIOS ─────────── */}
      <section id="beneficios" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-primary font-semibold">· 02 · Beneficios medibles</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-[-0.02em] max-w-2xl">Velocidad, transparencia y trazabilidad en cada interacción.</h2>
          </div>
          <div className="text-xs text-muted-foreground max-w-xs">Datos reales de la base de prototipo · se actualizan en vivo cada 15 segundos.</div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-3xl overflow-hidden border shadow-[0_30px_80px_-40px_rgba(16,24,40,0.3)]" data-testid="landing-stats">
          {[["Reportes ciudadanos", s?.reports, null, "geolocalizados con folio único", MapPin],
            ["Bloques anclados", s?.ledger, null, s?.chain_valid ? "cadena de auditoría íntegra" : "cadena verificable", Link2],
            ["Recaudación digital", s?.revenue, (v) => money(v).replace(".00", ""), "liquidada en segundos", Wallet],
            ["Reportes resueltos", s?.resolved, null, `${s?.open ?? "—"} en atención ahora`, CheckCircle2]].map(([l, v, f, h, I]) => (
            <div key={l} className="relative bg-white p-6 md:p-8 group overflow-hidden">
              <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity"><I className="h-14 w-14" /></div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground font-mono">{l}</div>
              <div className="text-4xl md:text-6xl font-bold font-display tracking-[-0.03em] mt-2 text-[#06151D]">{v == null ? "—" : <Counter value={v} format={f || undefined} />}</div>
              <div className="text-xs text-muted-foreground mt-2">{h}</div>
              <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-[#007C91] to-transparent opacity-40" />
            </div>
          ))}
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {[[Zap, "Folio en menos de 1 segundo", "El ciudadano recibe su folio y confirmación por WhatsApp al instante."],
            [ShieldCheck, "Inalterable por diseño", "Cada cambio se encadena por hash: cualquier alteración es detectable públicamente."],
            [Crosshair, "Precisión de metros", "GPS de alta precisión y dirección automática para reportes, multas y cuadrillas."]].map(([I, t, d]) => (
            <div key={t} className="relative rounded-2xl border bg-white p-6 tilt-card overflow-hidden">
              <span className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-[#DFF3F6] opacity-50" />
              <I className="h-6 w-6 text-primary relative" /><div className="mt-3 font-semibold text-lg relative">{t}</div><p className="text-sm text-muted-foreground mt-1 relative">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────── MODULOS ─────────── */}
      <section id="modulos" className="relative bg-[#06151D] text-white overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#7fd6e2] font-semibold">· 03 · Siete módulos · un ecosistema</div>
              <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-[-0.02em] max-w-2xl">Todo lo que una entidad necesita, conectado.</h2>
            </div>
            <Button variant="outline" className="rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white" onClick={login} data-testid="modules-login-cta">Entrar a la plataforma <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m, i) => {
              const featured = i === 0;
              return (
                <div key={m.t} className={`group relative rounded-2xl p-6 overflow-hidden tilt-card ${featured ? "lg:col-span-2 bg-gradient-to-br from-[#0F4C5C] to-[#07323D] border border-[#7fd6e2]/30 glow-border" : "glass-dark"}`} data-testid={`landing-module-${m.tag}`}>
                  <span className="corner-mark tl" /><span className="corner-mark tr" /><span className="corner-mark bl" /><span className="corner-mark br" />
                  <div className="flex items-center justify-between">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${featured ? "bg-[#7fd6e2]/20 text-[#7fd6e2]" : "bg-white/5 border border-white/10 text-[#7fd6e2]"}`}><m.icon className="h-5 w-5" /></span>
                    <span className="font-mono text-xs text-white/40">{m.tag}</span>
                  </div>
                  <div className={`mt-5 font-semibold font-display ${featured ? "text-2xl" : "text-lg"}`}>{m.t}</div>
                  <p className="text-sm mt-2 text-white/60 leading-relaxed">{m.d}</p>
                  <div className="flex items-center justify-between mt-4">
                    {m.sim ? <span className="inline-block rounded border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[10px] tracking-wide text-amber-300">INTEGRACIÓN SIMULADA</span> : <span />}
                    <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-[#7fd6e2] group-hover:translate-x-1 transition-all" />
                  </div>
                  {featured && (
                    <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 text-[11px] text-white/60">
                      <span className="flex items-center gap-1"><Crosshair className="h-3 w-3 text-[#7fd6e2]" />GPS ±5 m</span>
                      <span className="flex items-center gap-1"><Fingerprint className="h-3 w-3 text-[#7fd6e2]" />SHA-256</span>
                      <span className="flex items-center gap-1"><BellRing className="h-3 w-3 text-[#7fd6e2]" />Notifica al instante</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── PLATAFORMA ─────────── */}
      <section id="plataforma" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-[11px] tracking-[0.28em] uppercase text-primary font-semibold">· 04 · Plataforma</div>
        <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-[-0.02em] max-w-2xl">Del ciudadano al registro inmutable en un solo flujo.</h2>

        <div className="mt-10 rounded-3xl border bg-white p-6 md:p-10 shadow-[0_30px_80px_-40px_rgba(16,24,40,0.25)]">
          <div className="relative">
            <div className="absolute left-[6%] right-[6%] top-6 h-px bg-gradient-to-r from-transparent via-[#007C91]/60 to-transparent hidden md:block" />
            <div className="absolute top-[19px] h-3 w-3 rounded-full bg-[#007C91] pipe-dot hidden md:block shadow-[0_0_12px_rgba(0,124,145,0.9)]" />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
              {PIPE.map(([I, l], i) => (
                <div key={l} className="relative flex flex-col items-center text-center">
                  <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-[#b9e2e8] bg-white text-[#006A7C]"><I className="h-5 w-5" /></span>
                  <div className="mt-3 text-sm font-medium">{l}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">paso {String(i + 1).padStart(2, "0")}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-[1fr_1.1fr] gap-6 items-start">
            <div className="rounded-2xl bg-[#06151D] text-[#c9eef3] p-5 font-mono text-xs leading-relaxed overflow-x-auto relative glow-border" data-testid="landing-code-sample">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-white/10">
                <span className="flex gap-1"><span className="h-2 w-2 rounded-full bg-[#ff5f57]" /><span className="h-2 w-2 rounded-full bg-[#febc2e]" /><span className="h-2 w-2 rounded-full bg-[#28c840]" /></span>
                <span className="text-white/50 text-[10px] ml-2">POST /api/reports → 201</span>
              </div>
              <div>{"{"}</div>
              <div>&nbsp;&nbsp;"folio": <span className="text-[#fce7c3]">"{sampleFolio}"</span>,</div>
              <div>&nbsp;&nbsp;"status": <span className="text-[#fce7c3]">"recibido"</span>,</div>
              <div>&nbsp;&nbsp;"data_hash": <span className="text-[#fce7c3]">"sha256:…"</span>,</div>
              <div>&nbsp;&nbsp;"nom151": {"{"} "standard": <span className="text-[#fce7c3]">"NOM-151-SCFI-2016"</span> {"}"},</div>
              <div>&nbsp;&nbsp;"anchor": {"{"} "network": <span className="text-[#fce7c3]">"polygon-amoy"</span> {"}"}</div>
              <div>{"}"}</div>
            </div>
            <div>
              <div className="flex flex-wrap gap-2">
                {STACK.map((t) => <span key={t} className="rounded-full border bg-[#F7FBFC] px-3 py-1 text-xs">{t}</span>)}
              </div>
              <div className="mt-6 space-y-2.5 text-sm">
                {[["Backend Python serverless-ready (AWS Lambda / Cloud Functions)", Server],
                  ["Webhooks firmados HMAC con procesamiento idempotente", Lock],
                  ["Verificación pública por QR, folio o huella de documento", Search]].map(([t, I]) => (
                  <div key={t} className="flex items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#DFF3F6] text-[#006A7C]"><I className="h-4 w-4" /></span>{t}</div>
                ))}
              </div>
              <Button variant="outline" className="mt-6 rounded-full" onClick={() => navigate("/verificar")} data-testid="landing-verify-cta"><Search className="h-4 w-4 mr-1" />Verificar un folio</Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── CTA final ─────────── */}
      <section className="relative tech-hero overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#7fd6e2] font-semibold">· 05 · Entra ahora</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-[-0.02em] text-white">Gobierno conectado, ciudadanía informada.</h2>
            <p className="text-white/65 mt-3 text-lg">Ciudadanos, funcionarios y agentes de campo en una sola sesión. Un ecosistema completo listo para producción.</p>
          </div>
          <div className="flex flex-col items-stretch gap-3">
            <Button size="lg" className="rounded-full h-14 px-10 bg-white text-[#06151D] hover:bg-[#DFF3F6] btn-press font-semibold text-base shadow-[0_14px_40px_-10px_rgba(127,214,226,0.6)]" onClick={login} data-testid="cta-login-button">
              <Server className="h-4 w-4 mr-2" />Entrar a HitchGob
            </Button>
            <button onClick={() => navigate("/verificar")} className="text-xs text-white/60 hover:text-white underline underline-offset-4 transition-colors">o verificar un folio sin iniciar sesión</button>
          </div>
        </div>
      </section>

      <footer className="bg-[#04131A] text-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-xs flex flex-wrap justify-between gap-3">
          <span>HitchGob · Gobierno de Quintana Roo · Prototipo funcional</span>
          <span>WhatsApp, Cuenta Nu, HitchDocs NOM-151 y Polygon en modo SIMULADO · Mapas © OpenStreetMap</span>
        </div>
      </footer>
    </div>
  );
}
